import { useState } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AccountBrand } from "../../../types";
import { RemoveCompetitorDialog } from "../brands/RemoveCompetitorDialog";
import { IconAdjustmentsHorizontal, IconRefresh } from "@tabler/icons-react";
import { LuPlus } from "react-icons/lu";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { RiSearchLine } from "@remixicon/react";
import { useGlobalStore } from "@/GlobalStore";

type Props = {
  brands: AccountBrand[];
  onSyncBrand: (brandId: string) => void;
  onBrandRemove: (brandId: string) => void;
  onBrandRollback: (brand: AccountBrand) => void;
  onAddCompetitorClick: () => void;
  onAddDemoAccountClick: (brandId: string) => Promise<void>;
  loading?: boolean;
  className?: string;
  disabled?: boolean;
};

const CompetitorsDropdown = ({
  brands,
  onSyncBrand,
  onBrandRemove,
  onBrandRollback,
  onAddCompetitorClick,
  onAddDemoAccountClick,
  loading = false,
  className,
  disabled = false,
}: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const permission = useGlobalStore((state) => state.permission);
  const hasAdDemoAccountPermission = permission["add_ad_demo_account"] === true;
  // Filter brands based on search term
  const filteredBrands = brands.filter((brand) =>
    brand.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handlePopoverChange = (open: boolean) => {
    if (open) {
      // Reset search term when opening
      setSearchTerm("");
    }
    setPopoverOpen(open);
  };

  return (
    <Popover open={popoverOpen} onOpenChange={handlePopoverChange}>
      <PopoverTrigger asChild>
        <Button
          variant="sq-outline"
          disabled={disabled}
          className={cn(
            "w-auto h-8 rounded-[8px] bg-white shadow-sm inline-flex px-[14px] py-[6px] items-center gap-[6px]",
            className,
          )}
        >
          <IconAdjustmentsHorizontal size={14} className="text-brand-primary-400" />
          <span className="text-[11px] text-brand-primary-800 font-medium leading-[19.157px] tracking-[0.24px]">
            Manage Competitors
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-[8px] p-0">
        <div className="text-[12px] text-brand-primary-800 font-medium leading-[19.157px] tracking-[0.24px] px-[9px] py-[6px]">
          Competitors
        </div>
        <Separator />
        <div className="flex items-center gap-2 px-[8px]">
          <div className="flex items-center flex-1">
            <RiSearchLine size={14} color="hsl(0 0% 45.1%)" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Search brand name"
              className="shadow-none border-0 outline-none ring-0 text-[12px] px-1 relative top-[1px] placeholder:text-brand-primary-400"
            />
          </div>
        </div>
        <Separator />
        <TooltipProvider>
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <LuLoaderCircle className="animate-spin" size={16} />
              </div>
            ) : filteredBrands.length === 0 ? (
              <div className="text-center py-4 text-[12px] text-brand-primary-600">
                {searchTerm
                  ? "No competitors match your search"
                  : "No competitors added yet"}
              </div>
            ) : (
              filteredBrands.map((brand) => (
                <div
                  key={brand.id}
                  className="group flex items-center space-x-2 h-[32px] px-[9px] rounded hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2 flex-1 overflow-hidden">
                    <img
                      src={brand.profile_image_url || "default-logo-path"}
                      alt={brand.name}
                      className="w-[20px] h-[20px] object-contain rounded"
                    />
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <span className="text-[12px] line-clamp-1 truncate text-brand-primary-new font-normal leading-[19.157px] tracking-[0.24px] flex-1">
                          {brand.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{brand.name}</p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="text-[12px] text-brand-primary-600 font-normal leading-[19.157px] tracking-[0.24px]">
                      {brand.sync_status === "pending" && brand.ad_count === 0
                        ? "-"
                        : `${brand.ad_count || 0} ads`}
                    </span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {hasAdDemoAccountPermission && (
                      <div className="hidden group-hover:block">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={brand.sync_status === "pending"}
                          className={cn("h-6 w-6 p-0", brand.sync_status && "flex")}
                          onClick={() => void onAddDemoAccountClick(brand.id)}
                        >
                          <LuPlus className="text-brand-primary-800" size={12} />
                        </Button>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={brand.sync_status === "pending"}
                      className={cn(
                        "h-6 w-6 p-0 hidden group-hover:flex",
                        brand.sync_status && "flex",
                      )}
                      onClick={() => onSyncBrand(brand.id)}
                    >
                      {brand.sync_status === "pending" ? (
                        <LuLoaderCircle
                          className="animate-spin text-brand-orange"
                          size={12}
                        />
                      ) : (
                        <IconRefresh className="text-brand-primary-800" size={12} />
                      )}
                    </Button>
                    <div className="hidden group-hover:block">
                      <RemoveCompetitorDialog
                        brandName={brand.name}
                        brandId={brand.id}
                        onRemove={() => onBrandRemove(brand.id)}
                        onRollback={() => onBrandRollback(brand)}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TooltipProvider>
        <Separator />
        <div className="flex items-center justify-start p-[9px]">
          <Button variant="outline" onClick={onAddCompetitorClick}>
            <LuPlus className="shrink-0" size={16} />
            <span className="mt-[1px]">Add Brand</span>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CompetitorsDropdown;
